import { countriesList, swapDomain } from '@archipro-website/localisation';
import type { SupportedCountry } from '@archipro-website/localisation';
import type { TopnavClientEnvironment } from '../types';

export const pathsThatShouldRedirectToHome = [
    'product',
    'professional',
    'project',
    'article',
];

/**
 * Query keys that encode a place filter. These are market-specific, so they
 * must not be copied onto the other country's domain.
 */
const LOCATION_SWITCH_QUERY_KEYS = new Set([
    'suburb',
    'district',
    'city',
    'region',
    'location-state',
    'country',
    'location',
    'latitude',
    'longitude',
    'currentLocation',
    'serviceableAreaID',
    'placeType',
]);

/**
 * Drops location from a path+search string before the country switcher
 * appends it to the other market. Category, sort, and other filters stay.
 */
export const stripLocationFromCountrySwitchPath = (
    pathAndSearch: string,
): string => {
    if (!pathAndSearch) {
        return pathAndSearch;
    }

    const queryIndex = pathAndSearch.indexOf('?');
    const pathname =
        queryIndex === -1 ? pathAndSearch : pathAndSearch.slice(0, queryIndex);
    const search = queryIndex === -1 ? '' : pathAndSearch.slice(queryIndex + 1);

    const cleanedPath =
        pathname
            .split('/')
            .filter((segment) => !segment.startsWith('location-'))
            .join('/') || (pathname.startsWith('/') ? '/' : '');

    if (!search) {
        return cleanedPath;
    }

    const params = new URLSearchParams(search);
    let removedLocationParam = false;
    for (const key of [...params.keys()]) {
        if (LOCATION_SWITCH_QUERY_KEYS.has(key)) {
            params.delete(key);
            removedLocationParam = true;
        }
    }

    if (!removedLocationParam) {
        return cleanedPath === pathname
            ? pathAndSearch
            : `${cleanedPath}?${search}`;
    }

    const query = params.toString();
    return query ? `${cleanedPath}?${query}` : cleanedPath;
};

/** Builds the target URL when switching from one country to another. */
export const buildCountryLink = (
    env: TopnavClientEnvironment,
    fromCountry: SupportedCountry,
    toCountry: SupportedCountry,
): string => {
    const host = env.host || countriesList[toCountry].domain;

    if (env.version === 'local') {
        return 'https://' + host;
    }

    return 'https://' + swapDomain(host, fromCountry, toCountry);
};
